import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { TrendingUp, DollarSign, Package, Users, AlertTriangle, RefreshCw, Trophy, Store } from "lucide-react";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");

export default function CommandCenter() {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(() => {
    api.get(`/dashboard/command-center?period=${period}`).then(({ data }) => { setData(data); setLastRefresh(new Date()); });
  }, [period]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, [load]);

  if (!data) return <div className="p-8 text-slate-400 text-center">Memuat Command Center...</div>;
  const t = data.totals;

  return (
    <div className="page-shell">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div data-workspace-redundant-header>
          <h1 className="text-2xl font-extrabold font-display flex items-center gap-2"><TrendingUp className="w-6 h-6 text-orange-500"/>Owner Command Center</h1>
          <p className="text-sm text-slate-500">Konsolidasi real-time seluruh outlet · auto-refresh 30 detik</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="cc-period-select" className="w-40"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hari ini</SelectItem>
              <SelectItem value="yesterday">Kemarin</SelectItem>
              <SelectItem value="7d">7 hari</SelectItem>
              <SelectItem value="30d">30 hari</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-1"/>Refresh</Button>
          <span className="text-xs text-slate-500 hidden md:block">Update: {lastRefresh?.toLocaleTimeString("id-ID")}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard label="Omzet" value={rupiah(t.sales)} icon={DollarSign} tone="emerald" testid="cc-sales"/>
        <KPICard label="HPP" value={rupiah(t.hpp)} icon={Package} tone="amber"/>
        <KPICard label="Gross Profit" value={rupiah(t.gross_profit)} icon={TrendingUp} tone="blue" testid="cc-gp"/>
        <KPICard label="Margin" value={`${t.margin_pct}%`} icon={TrendingUp} tone="violet"/>
        <KPICard label="Pengeluaran" value={rupiah(t.expenses)} icon={DollarSign} tone="rose"/>
        <KPICard label="Net Profit" value={rupiah(t.net_profit)} icon={DollarSign} tone={t.net_profit >= 0 ? "emerald" : "rose"} testid="cc-net"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard label="Transaksi" value={t.trx_count} icon={DollarSign} sub="pada periode ini"/>
        <StatCard label="Karyawan Aktif" value={data.active_employees} icon={Users} sub="siap payroll"/>
        <StatCard label="Estimasi Payroll" value={rupiah(data.payroll_estimate_month)} icon={Users} sub="bulan berjalan (base salary)"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2"><Store className="w-4 h-4 text-orange-500"/><div className="font-semibold">Performance per Outlet</div></div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 border-b"><tr><th className="text-left py-2">Outlet</th><th className="text-right">Sales</th><th className="text-right">GP</th><th className="text-right">Trx</th></tr></thead>
            <tbody>
              {data.outlets.map(o => (
                <tr key={o.outlet_id} className="border-b" data-testid={`cc-outlet-${o.outlet_id}`}>
                  <td className="py-2">
                    <div className="font-medium">{o.name}</div>
                    <div className="text-xs text-slate-500">{o.brand}</div>
                  </td>
                  <td className="text-right num font-semibold">{rupiah(o.sales)}</td>
                  <td className="text-right num text-emerald-600">{rupiah(o.gp)}</td>
                  <td className="text-right num">{o.trx}</td>
                </tr>
              ))}
              {!data.outlets.length && <tr><td colSpan="4" className="py-8 text-center text-slate-400">Tidak ada penjualan pada periode ini</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2"><Trophy className="w-4 h-4 text-orange-500"/><div className="font-semibold">Top Products</div></div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 border-b"><tr><th className="text-left py-2">Produk</th><th className="text-right">Qty</th><th className="text-right">Sales</th></tr></thead>
            <tbody>
              {data.top_products.map(p => (
                <tr key={p.product_id} className="border-b">
                  <td className="py-2">{p.name}</td>
                  <td className="text-right num">{p.qty}</td>
                  <td className="text-right num font-semibold">{rupiah(p.sales)}</td>
                </tr>
              ))}
              {!data.top_products.length && <tr><td colSpan="3" className="py-8 text-center text-slate-400">-</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>

      {(data.critical_stock.products.length > 0 || data.critical_stock.ingredients.length > 0) && (
        <Card className="p-4 border-rose-200 bg-rose-50">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-rose-600"/><div className="font-semibold text-rose-700">Stok Kritis (harus segera dibelanjakan)</div></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold text-rose-700 mb-1">Produk ({data.critical_stock.products.length})</div>
              <div className="max-h-48 overflow-auto space-y-1">
                {data.critical_stock.products.map(p => (
                  <div key={p.id} className="flex justify-between text-sm border-b py-1">
                    <span>{p.name}</span>
                    <Badge variant="outline" className="text-rose-600 border-rose-300">Stok: {p.stock} · min {p.min_stock}</Badge>
                  </div>
                ))}
                {!data.critical_stock.products.length && <div className="text-xs text-slate-500">Aman</div>}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-rose-700 mb-1">Bahan Baku ({data.critical_stock.ingredients.length})</div>
              <div className="max-h-48 overflow-auto space-y-1">
                {data.critical_stock.ingredients.map(i => (
                  <div key={i.id} className="flex justify-between text-sm border-b py-1">
                    <span>{i.name}</span>
                    <Badge variant="outline" className="text-rose-600 border-rose-300">Stok: {i.stock}{i.unit} · min {i.min_stock}</Badge>
                  </div>
                ))}
                {!data.critical_stock.ingredients.length && <div className="text-xs text-slate-500">Aman</div>}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function KPICard({ label, value, icon: Icon, tone, testid }) {
  const tones = {
    emerald: "bg-emerald-500", amber: "bg-amber-500", blue: "bg-blue-500",
    violet: "bg-violet-500", rose: "bg-rose-500", slate: "bg-slate-500",
  };
  return (
    <Card className="p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
        <div className={`w-6 h-6 rounded ${tones[tone] || tones.slate} flex items-center justify-center`}><Icon className="w-3.5 h-3.5 text-white"/></div>
      </div>
      <div className="text-lg font-extrabold font-display num">{value}</div>
    </Card>
  );
}

function StatCard({ label, value, icon: Icon, sub }) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
          <div className="text-2xl font-extrabold font-display num mt-1">{value}</div>
          <div className="text-xs text-slate-400">{sub}</div>
        </div>
        <div className="w-9 h-9 rounded bg-orange-50 flex items-center justify-center"><Icon className="w-4 h-4 text-orange-500"/></div>
      </div>
    </Card>
  );
}
